import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, MessageSquare, CheckCircle, MapPin, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import { services, bookingTimeSlots, companyInfo } from '../data/mockData';
import { toast } from 'sonner';
import axios from 'axios';
import { AddressAutocomplete } from './AddressAutocomplete';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    date: '',
    timeSlot: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/booking`, formData);
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error('Failed to submit booking. Please try calling us instead.');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      service: '',
      date: '',
      timeSlot: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center text-green-600 hover:text-green-700 font-semibold mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </Link>

        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Book Your Service
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Schedule Your <span className="text-green-600">Free Estimate</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Fill out the form below and our team will contact you to confirm your appointment.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-2 border-green-500/20">
                <CardContent className="p-6">
                  <h3 className="font-bold text-xl text-gray-900 mb-4">Contact Us Directly</h3>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="bg-green-100 p-2 rounded-lg">
                        <Phone className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <a href={`tel:${companyInfo.phone.replace(/\D/g, '')}`} className="text-green-600 font-semibold hover:text-green-700 block">
                          {companyInfo.phone}
                        </a>
                        <a href={`tel:${companyInfo.phoneSecondary.replace(/\D/g, '')}`} className="text-green-600 font-semibold hover:text-green-700 block">
                          {companyInfo.phoneSecondary}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="bg-green-100 p-2 rounded-lg">
                        <Clock className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-gray-700 font-medium">{companyInfo.hours}</p>
                        <p className="text-sm text-gray-500">24/7 Emergency Service</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="bg-green-100 p-2 rounded-lg">
                        <MapPin className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-gray-700 font-medium">{companyInfo.location}</p>
                        <p className="text-sm text-gray-500">& Surrounding Areas</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-500/20 bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="p-6">
                  <h3 className="font-bold text-xl mb-3">Why Book With Us?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>Free, no-obligation estimates</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>Fully licensed & insured</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>Same-day response available</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>10+ years serving Nashville</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Booking Form */}
            <div className="lg:col-span-2">
              <Card className="border-2 border-green-500/20 shadow-xl">
                <CardContent className="p-8">
                  {isSubmitted ? (
                    <div className="py-16 text-center">
                      <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-14 h-14 text-white" />
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-3">Booking Received!</h3>
                      <p className="text-gray-600 text-lg mb-8">
                        Thank you for your request. We'll contact you shortly to confirm your appointment.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button 
                          onClick={resetForm}
                          className="bg-green-500 hover:bg-green-600 text-white font-bold px-8 py-3"
                        >
                          Book Another Service
                        </Button>
                        <Link to="/">
                          <Button variant="outline" className="border-2 border-green-500 text-green-600 hover:bg-green-50 font-bold px-8 py-3">
                            Return to Home
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <User className="w-4 h-4 inline mr-2" />
                            Full Name *
                          </label>
                          <Input
                            required
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder="John Doe"
                            className="border-2 focus:border-green-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <Phone className="w-4 h-4 inline mr-2" />
                            Phone Number *
                          </label>
                          <Input
                            required
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            placeholder="(615) 123-4567"
                            className="border-2 focus:border-green-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <Mail className="w-4 h-4 inline mr-2" />
                          Email Address *
                        </label>
                        <Input
                          required
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          placeholder="john@example.com"
                          className="border-2 focus:border-green-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <MapPin className="w-4 h-4 inline mr-2" />
                          Service Address *
                        </label>
                        <AddressAutocomplete
                          value={formData.address}
                          onChange={(value) => handleChange('address', value)}
                          required
                          placeholder="Enter your service address"
                        />
                        <p className="text-xs text-gray-500 mt-1">Start typing and select from dropdown</p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Service Needed *
                        </label>
                        <Select value={formData.service} onValueChange={(value) => handleChange('service', value)}>
                          <SelectTrigger className="border-2 focus:border-green-500">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map(service => (
                              <SelectItem key={service.id} value={service.title}>
                                {service.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <Calendar className="w-4 h-4 inline mr-2" />
                            Preferred Date *
                          </label>
                          <Input
                            required
                            type="date"
                            value={formData.date}
                            onChange={(e) => handleChange('date', e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="border-2 focus:border-green-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <Clock className="w-4 h-4 inline mr-2" />
                            Time Slot *
                          </label>
                          <Select value={formData.timeSlot} onValueChange={(value) => handleChange('timeSlot', value)}>
                            <SelectTrigger className="border-2 focus:border-green-500">
                              <SelectValue placeholder="Select time" />
                            </SelectTrigger>
                            <SelectContent>
                              {bookingTimeSlots.map(slot => (
                                <SelectItem key={slot} value={slot}>
                                  {slot}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <MessageSquare className="w-4 h-4 inline mr-2" />
                          Additional Details
                        </label>
                        <Textarea
                          value={formData.message}
                          onChange={(e) => handleChange('message', e.target.value)}
                          placeholder="Tell us about your project, any specific concerns, or questions..."
                          rows={4}
                          className="border-2 focus:border-green-500"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full bg-green-500 hover:bg-green-600 text-white font-bold text-lg py-6"
                      >
                        Submit Booking Request
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
